// Campus Infrastructure Service - Manages buildings, floors, rooms, and terrains

import { apiGet, apiPost, apiPatch, apiDelete, API_BASE } from '@/utils/api';

// ==================== ENUMS & TYPES ====================

export enum RoomCategory {
  LOCAL_ENSEIGNEMENT = 'LOCAL_ENSEIGNEMENT',
  LOCAL_ADMINISTRATIF = 'LOCAL_ADMINISTRATIF',
  BIBLIOTHEQUE = 'BIBLIOTHEQUE',
  ESPACE_SOCIOCULTUREL = 'ESPACE_SOCIOCULTUREL',
  ESPACE_SPORTIF = 'ESPACE_SPORTIF',
  ESPACE_EXTERIEUR_COMMUN = 'ESPACE_EXTERIEUR_COMMUN',
}

export const ROOM_CATEGORY_LABELS: Record<RoomCategory, string> = {
  [RoomCategory.LOCAL_ENSEIGNEMENT]: "Locaux d'enseignement",
  [RoomCategory.LOCAL_ADMINISTRATIF]: 'Locaux administratifs et techniques',
  [RoomCategory.BIBLIOTHEQUE]: 'Bibliotheque et documentation',
  [RoomCategory.ESPACE_SOCIOCULTUREL]: 'Espaces socioculturels',
  [RoomCategory.ESPACE_SPORTIF]: 'Espaces sportifs',
  [RoomCategory.ESPACE_EXTERIEUR_COMMUN]: 'Espaces exterieurs et communs',
};

export const ROOM_SUB_CATEGORIES: Record<RoomCategory, { value: string; label: string }[]> = {
  [RoomCategory.LOCAL_ENSEIGNEMENT]: [
    { value: 'SALLE_COURS', label: 'Salle de cours' },
    { value: 'SALLE_TD', label: 'Salle de TD' },
    { value: 'SALLE_TP', label: 'Salle de TP' },
    { value: 'AMPHITHEATRE', label: 'Amphitheatre' },
    { value: 'LABO_INFORMATIQUE', label: 'Laboratoire informatique' },
    { value: 'LABO_LANGUES', label: 'Laboratoire de langues' },
    { value: 'LABO_SCIENTIFIQUE', label: 'Laboratoire scientifique' },
    { value: 'SALLE_CONFERENCE', label: 'Salle de conference' },
    { value: 'SALLE_VISIOCONFERENCE', label: 'Salle de visioconference' },
    { value: 'SALLE_MULTIMEDIA', label: 'Salle multimedia' },
    { value: 'ATELIER', label: 'Atelier' },
    { value: 'SALLE_EXAMEN', label: "Salle d'examen" },
    { value: 'SALLE_POLYVALENTE', label: 'Salle polyvalente' },
  ],
  [RoomCategory.LOCAL_ADMINISTRATIF]: [
    { value: 'BUREAU_DIRECTION', label: 'Bureau de direction' },
    { value: 'BUREAU_ADMINISTRATIF', label: 'Bureau administratif' },
    { value: 'BUREAU_ENSEIGNANTS', label: 'Bureau des enseignants' },
    { value: 'BUREAU_CHEF_FILIERE', label: 'Bureau Chef de Filiere' },
    { value: 'ACCUEIL_RECEPTION', label: 'Accueil / Reception' },
    { value: 'SALLE_REUNION', label: 'Salle de reunion' },
    { value: 'SALLE_PROFESSEURS', label: 'Salle des professeurs' },
    { value: 'SECRETARIAT', label: 'Secretariat' },
    { value: 'SERVICE_SCOLARITE', label: 'Service scolarite' },
    { value: 'SERVICE_FINANCIER', label: 'Service financier' },
    { value: 'SALLE_ARCHIVES', label: "Salle d'archives" },
    { value: 'MAGASIN_STOCKAGE', label: 'Magasin / Stockage' },
    { value: 'BUREAU_TIRAGES', label: 'Bureau des tirages' },
    { value: 'LOCAL_TECHNIQUE', label: 'Local technique' },
    { value: 'INFIRMERIE', label: 'Infirmerie' },
  ],
  [RoomCategory.BIBLIOTHEQUE]: [
    { value: 'SALLE_RAYONNAGE', label: 'Salle de rayonnage' },
    { value: 'SALLE_LECTURE', label: 'Salle de lecture' },
    { value: 'ESPACE_MULTIMEDIA_BIB', label: 'Espace multimedia bibliotheque' },
    { value: 'SALLE_TRAVAIL_GROUPE', label: 'Salle de travail en groupe' },
  ],
  [RoomCategory.ESPACE_SOCIOCULTUREL]: [
    { value: 'CAFETERIA_BUVETTE', label: 'Cafeteria / Buvette' },
    { value: 'RESTAURANT', label: 'Restaurant' },
    { value: 'FOYER_ETUDIANTS', label: 'Foyer des etudiants' },
    { value: 'SALLE_CLUBS', label: 'Salle des clubs' },
    { value: 'SALLE_PRIERE', label: 'Salle de priere' },
    { value: 'ESPACE_COWORKING', label: 'Espace coworking' },
  ],
  [RoomCategory.ESPACE_SPORTIF]: [
    { value: 'SALLE_SPORT_GYMNASE', label: 'Salle de sport / Gymnase' },
    { value: 'TERRAIN_FOOTBALL', label: 'Terrain de football' },
    { value: 'TERRAIN_BASKETBALL', label: 'Terrain de basketball' },
    { value: 'TERRAIN_VOLLEYBALL', label: 'Terrain de volleyball' },
    { value: 'COURT_TENNIS', label: 'Court de tennis' },
    { value: 'TERRAIN_MULTISPORTS', label: 'Terrain multisports' },
    { value: 'PISCINE', label: 'Piscine' },
    { value: 'PISTE_ATHLETISME', label: "Piste d'athletisme" },
    { value: 'SALLE_FITNESS', label: 'Salle de fitness' },
  ],
  [RoomCategory.ESPACE_EXTERIEUR_COMMUN]: [
    { value: 'COUR', label: 'Cour' },
    { value: 'ESPACE_VERT_JARDIN', label: 'Espace vert / Jardin' },
    { value: 'PARKING', label: 'Parking' },
    { value: 'PARKING_VELOS', label: 'Parking velos' },
    { value: 'HALL_ENTREE', label: "Hall d'entree" },
    { value: 'COULOIRS_CIRCULATION', label: 'Couloirs / Circulation' },
    { value: 'ESCALIERS', label: 'Escaliers' },
    { value: 'ASCENSEUR', label: 'Ascenseur' },
    { value: 'SANITAIRES_HOMMES', label: 'Sanitaires hommes' },
    { value: 'SANITAIRES_FEMMES', label: 'Sanitaires femmes' },
    { value: 'SANITAIRES_PMR', label: 'Sanitaires PMR' },
  ],
};

export const TERRAIN_TYPES = [
  { value: 'TERRAIN_FOOTBALL', label: 'Terrain de football' },
  { value: 'TERRAIN_BASKETBALL', label: 'Terrain de basketball' },
  { value: 'TERRAIN_VOLLEYBALL', label: 'Terrain de volleyball' },
  { value: 'COURT_TENNIS', label: 'Court de tennis' },
  { value: 'TERRAIN_MULTISPORTS', label: 'Terrain multisports' },
  { value: 'PISTE_ATHLETISME', label: "Piste d'athletisme" },
  { value: 'PISCINE', label: 'Piscine' },
  { value: 'PARKING', label: 'Parking' },
  { value: 'PARKING_VELOS', label: 'Parking velos' },
  { value: 'ESPACE_VERT', label: 'Espace vert / Jardin' },
  { value: 'COUR', label: 'Cour' },
  { value: 'AUTRE', label: 'Autre' },
];

// ==================== INTERFACES ====================

export interface Room {
  id: string;
  floorId: string;
  name: string;
  category: RoomCategory;
  subCategory: string;
  capacity?: number;
  area?: number;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Floor {
  id: string;
  buildingId: string;
  floorNumber: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  rooms?: Room[];
}

export interface Building {
  id: string;
  campusId: string;
  name?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  floors?: Floor[];
}

export interface Terrain {
  id: string;
  campusId: string;
  name: string;
  type: string;
  area?: number;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CampusWithInfrastructure {
  id: string;
  tenantId: string;
  name: string;
  sigle?: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  receptionPhoneCountry: string;
  receptionPhoneNumber: string;
  isMainCampus: boolean;
  isActive: boolean;
  hasMultipleBuildings: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  buildings: Building[];
  terrains: Terrain[];
  stats?: {
    buildingCount: number;
    floorCount: number;
    roomCount: number;
    terrainCount: number;
  };
}

// ==================== DTOs ====================

export interface ConfigureCampusInfrastructureDto {
  campusId: string;
  hasMultipleBuildings: boolean;
  buildings?: { name: string }[];
  hasTerrains: boolean;
  terrains?: { name: string; type: string }[];
}

export interface ConfigureBuildingFloorsDto {
  hasBasement: boolean;
  hasGroundFloor: boolean;
  numberOfFloors: number;
}

export interface RoomConfigDto {
  name: string;
  category: RoomCategory;
  subCategory: string;
  capacity?: number;
}

export interface ConfigureFloorRoomsDto {
  rooms: RoomConfigDto[];
}

export interface CreateBuildingDto {
  name?: string;
  sortOrder?: number;
}

export interface UpdateBuildingDto {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateFloorDto {
  floorNumber: number;
  name: string;
  sortOrder?: number;
}

export interface UpdateFloorDto {
  floorNumber?: number;
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateRoomDto {
  name: string;
  category: RoomCategory;
  subCategory: string;
  capacity?: number;
  area?: number;
  sortOrder?: number;
}

export interface UpdateRoomDto {
  name?: string;
  category?: RoomCategory;
  subCategory?: string;
  capacity?: number;
  area?: number;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateTerrainDto {
  name: string;
  type: string;
  area?: number;
  sortOrder?: number;
}

export interface UpdateTerrainDto {
  name?: string;
  type?: string;
  area?: number;
  sortOrder?: number;
  isActive?: boolean;
}

// ==================== API FUNCTIONS ====================

// Legacy functions with token parameter - kept for backward compatibility

// Get all campuses with infrastructure summary
export async function fetchCampusesWithInfrastructure(
  token: string
): Promise<CampusWithInfrastructure[]> {
  const response = await fetch(`${API_BASE}/campus-infrastructure/campuses`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch campuses with infrastructure');
  }

  return response.json();
}

// Get single campus with full infrastructure
export async function fetchCampusInfrastructure(
  token: string,
  campusId: string
): Promise<CampusWithInfrastructure> {
  const response = await fetch(`${API_BASE}/campus-infrastructure/campuses/${campusId}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch campus infrastructure');
  }

  return response.json();
}

// Configure campus infrastructure (initial setup)
export async function configureCampusInfrastructure(
  token: string,
  data: ConfigureCampusInfrastructureDto
): Promise<CampusWithInfrastructure> {
  const response = await fetch(`${API_BASE}/campus-infrastructure/campuses/configure`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to configure campus infrastructure');
  }

  return response.json();
}

// ==================== BUILDINGS ====================

export async function fetchBuildings(token: string, campusId: string): Promise<Building[]> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch buildings');
  }

  return response.json();
}

export async function createBuilding(
  token: string,
  campusId: string,
  data: CreateBuildingDto
): Promise<{ success: boolean; message: string; data: Building }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create building');
  }

  return response.json();
}

export async function updateBuilding(
  token: string,
  campusId: string,
  buildingId: string,
  data: UpdateBuildingDto
): Promise<{ success: boolean; message: string; data: Building }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update building');
  }

  return response.json();
}

export async function deleteBuilding(
  token: string,
  campusId: string,
  buildingId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete building');
  }

  return response.json();
}

// ==================== FLOORS ====================

export async function fetchFloors(
  token: string,
  campusId: string,
  buildingId: string
): Promise<Floor[]> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch floors');
  }

  return response.json();
}

export async function configureBuildingFloors(
  token: string,
  campusId: string,
  buildingId: string,
  data: ConfigureBuildingFloorsDto
): Promise<Floor[]> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/configure`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to configure building floors');
  }

  return response.json();
}

export async function createFloor(
  token: string,
  campusId: string,
  buildingId: string,
  data: CreateFloorDto
): Promise<{ success: boolean; message: string; data: Floor }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create floor');
  }

  return response.json();
}

export async function updateFloor(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string,
  data: UpdateFloorDto
): Promise<{ success: boolean; message: string; data: Floor }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update floor');
  }

  return response.json();
}

export async function deleteFloor(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete floor');
  }

  return response.json();
}

// ==================== ROOMS ====================

export async function fetchRooms(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string
): Promise<Room[]> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch rooms');
  }

  return response.json();
}

export async function configureFloorRooms(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string,
  data: ConfigureFloorRoomsDto
): Promise<Room[]> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms/configure`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to configure floor rooms');
  }

  return response.json();
}

export async function createRoom(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string,
  data: CreateRoomDto
): Promise<{ success: boolean; message: string; data: Room }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create room');
  }

  return response.json();
}

export async function updateRoom(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string,
  roomId: string,
  data: UpdateRoomDto
): Promise<{ success: boolean; message: string; data: Room }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms/${roomId}`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update room');
  }

  return response.json();
}

export async function deleteRoom(
  token: string,
  campusId: string,
  buildingId: string,
  floorId: string,
  roomId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms/${roomId}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete room');
  }

  return response.json();
}

// ==================== TERRAINS ====================

export async function fetchTerrains(token: string, campusId: string): Promise<Terrain[]> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/terrains`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch terrains');
  }

  return response.json();
}

export async function createTerrain(
  token: string,
  campusId: string,
  data: CreateTerrainDto
): Promise<{ success: boolean; message: string; data: Terrain }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/terrains`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create terrain');
  }

  return response.json();
}

export async function updateTerrain(
  token: string,
  campusId: string,
  terrainId: string,
  data: UpdateTerrainDto
): Promise<{ success: boolean; message: string; data: Terrain }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/terrains/${terrainId}`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update terrain');
  }

  return response.json();
}

export async function deleteTerrain(
  token: string,
  campusId: string,
  terrainId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    `${API_BASE}/campus-infrastructure/campuses/${campusId}/terrains/${terrainId}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete terrain');
  }

  return response.json();
}

// ==================== NEW API-BASED FUNCTIONS ====================
// These use the centralized api utilities and don't require token parameter

export async function getCampusesWithInfrastructure(): Promise<CampusWithInfrastructure[]> {
  return apiGet<CampusWithInfrastructure[]>('/campus-infrastructure/campuses');
}

export async function getCampusInfrastructure(campusId: string): Promise<CampusWithInfrastructure> {
  return apiGet<CampusWithInfrastructure>(`/campus-infrastructure/campuses/${campusId}`);
}

export async function configureCampusInfrastructureNew(
  data: ConfigureCampusInfrastructureDto
): Promise<CampusWithInfrastructure> {
  return apiPost<CampusWithInfrastructure>('/campus-infrastructure/campuses/configure', data);
}

export async function getBuildings(campusId: string): Promise<Building[]> {
  return apiGet<Building[]>(`/campus-infrastructure/campuses/${campusId}/buildings`);
}

export async function createBuildingNew(
  campusId: string,
  data: CreateBuildingDto
): Promise<{ success: boolean; message: string; data: Building }> {
  return apiPost<{ success: boolean; message: string; data: Building }>(
    `/campus-infrastructure/campuses/${campusId}/buildings`,
    data
  );
}

export async function updateBuildingNew(
  campusId: string,
  buildingId: string,
  data: UpdateBuildingDto
): Promise<{ success: boolean; message: string; data: Building }> {
  return apiPatch<{ success: boolean; message: string; data: Building }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}`,
    data
  );
}

export async function deleteBuildingNew(
  campusId: string,
  buildingId: string
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}`
  );
}

export async function getFloors(campusId: string, buildingId: string): Promise<Floor[]> {
  return apiGet<Floor[]>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors`
  );
}

export async function configureBuildingFloorsNew(
  campusId: string,
  buildingId: string,
  data: ConfigureBuildingFloorsDto
): Promise<Floor[]> {
  return apiPost<Floor[]>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/configure`,
    data
  );
}

export async function createFloorNew(
  campusId: string,
  buildingId: string,
  data: CreateFloorDto
): Promise<{ success: boolean; message: string; data: Floor }> {
  return apiPost<{ success: boolean; message: string; data: Floor }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors`,
    data
  );
}

export async function updateFloorNew(
  campusId: string,
  buildingId: string,
  floorId: string,
  data: UpdateFloorDto
): Promise<{ success: boolean; message: string; data: Floor }> {
  return apiPatch<{ success: boolean; message: string; data: Floor }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}`,
    data
  );
}

export async function deleteFloorNew(
  campusId: string,
  buildingId: string,
  floorId: string
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}`
  );
}

export async function getRooms(
  campusId: string,
  buildingId: string,
  floorId: string
): Promise<Room[]> {
  return apiGet<Room[]>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms`
  );
}

export async function configureFloorRoomsNew(
  campusId: string,
  buildingId: string,
  floorId: string,
  data: ConfigureFloorRoomsDto
): Promise<Room[]> {
  return apiPost<Room[]>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms/configure`,
    data
  );
}

export async function createRoomNew(
  campusId: string,
  buildingId: string,
  floorId: string,
  data: CreateRoomDto
): Promise<{ success: boolean; message: string; data: Room }> {
  return apiPost<{ success: boolean; message: string; data: Room }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms`,
    data
  );
}

export async function updateRoomNew(
  campusId: string,
  buildingId: string,
  floorId: string,
  roomId: string,
  data: UpdateRoomDto
): Promise<{ success: boolean; message: string; data: Room }> {
  return apiPatch<{ success: boolean; message: string; data: Room }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms/${roomId}`,
    data
  );
}

export async function deleteRoomNew(
  campusId: string,
  buildingId: string,
  floorId: string,
  roomId: string
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(
    `/campus-infrastructure/campuses/${campusId}/buildings/${buildingId}/floors/${floorId}/rooms/${roomId}`
  );
}

export async function getTerrains(campusId: string): Promise<Terrain[]> {
  return apiGet<Terrain[]>(`/campus-infrastructure/campuses/${campusId}/terrains`);
}

export async function createTerrainNew(
  campusId: string,
  data: CreateTerrainDto
): Promise<{ success: boolean; message: string; data: Terrain }> {
  return apiPost<{ success: boolean; message: string; data: Terrain }>(
    `/campus-infrastructure/campuses/${campusId}/terrains`,
    data
  );
}

export async function updateTerrainNew(
  campusId: string,
  terrainId: string,
  data: UpdateTerrainDto
): Promise<{ success: boolean; message: string; data: Terrain }> {
  return apiPatch<{ success: boolean; message: string; data: Terrain }>(
    `/campus-infrastructure/campuses/${campusId}/terrains/${terrainId}`,
    data
  );
}

export async function deleteTerrainNew(
  campusId: string,
  terrainId: string
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(
    `/campus-infrastructure/campuses/${campusId}/terrains/${terrainId}`
  );
}

// Helper function to get sub-category label
export function getSubCategoryLabel(category: RoomCategory, subCategory: string): string {
  const subCategories = ROOM_SUB_CATEGORIES[category];
  const found = subCategories?.find((sc) => sc.value === subCategory);
  return found?.label || subCategory;
}

// Helper function to get terrain type label
export function getTerrainTypeLabel(type: string): string {
  const found = TERRAIN_TYPES.find((t) => t.value === type);
  return found?.label || type;
}

// Flat room structure for easier usage in dropdowns
export interface RoomFlat {
  id: string;
  name: string;
  fullName: string; // includes building/floor info
  category: RoomCategory;
  subCategory: string;
  capacity?: number;
  buildingName?: string;
  floorName: string;
}

// Teaching rooms of a campus, flat list for the planning dropdowns. Served by the
// schedule module so every planner (incl. coordinateur pédagogique) can assign a
// room without holding the infrastructure-management permission.
export async function fetchTeachingRooms(_token: string, campusId: string): Promise<RoomFlat[]> {
  return apiGet<RoomFlat[]>(`/schedule/rooms?campusId=${encodeURIComponent(campusId)}`);
}

// Get all teaching rooms for a campus (flat list for dropdowns) - New API-based
export async function getTeachingRooms(campusId: string): Promise<RoomFlat[]> {
  // Fetch the full campus infrastructure
  const campus = await getCampusInfrastructure(campusId);

  const rooms: RoomFlat[] = [];

  // Traverse buildings -> floors -> rooms
  for (const building of campus.buildings || []) {
    for (const floor of building.floors || []) {
      for (const room of floor.rooms || []) {
        // Only include teaching rooms (LOCAL_ENSEIGNEMENT category)
        if (room.category === RoomCategory.LOCAL_ENSEIGNEMENT && room.isActive) {
          const buildingName = building.name || 'Batiment principal';
          rooms.push({
            id: room.id,
            name: room.name,
            fullName: campus.hasMultipleBuildings
              ? `${room.name} (${buildingName} - ${floor.name})`
              : `${room.name} (${floor.name})`,
            category: room.category,
            subCategory: room.subCategory,
            capacity: room.capacity,
            buildingName: building.name,
            floorName: floor.name,
          });
        }
      }
    }
  }

  return rooms;
}
